const BACKUP_KEY = 'cart_unselected_backup';
const SELECTED_STATE_KEY = 'cart_selected_state';
const BACKUP_EXPIRY_MS = 24 * 60 * 60 * 1000;

class CartSelectiveCheckout {
  /** @type {Set<string>} */
  #selectedKeys = new Set();

  constructor() {
    this.#init();
  }

  async #init() {
    await this.#restoreUnselectedItems();
    this.#initCheckboxes();
    this.#bindEvents();
    this.#updateSummary();
  }

  // ---------------------------------------------------------------------------
  // Restoration: recover items removed before a previous checkout attempt
  // ---------------------------------------------------------------------------

  async #restoreUnselectedItems() {
    const raw = localStorage.getItem(BACKUP_KEY);
    if (!raw) return;

    let backup;
    try {
      backup = JSON.parse(raw);
    } catch {
      localStorage.removeItem(BACKUP_KEY);
      return;
    }

    const expired = Date.now() - backup.timestamp > BACKUP_EXPIRY_MS;
    const hasLegacyItems = backup.items?.length > 0;
    const hasFullOrder = Array.isArray(backup.fullLineOrder) && backup.fullLineOrder.length > 0;

    if (expired || (!hasLegacyItems && !hasFullOrder)) {
      localStorage.removeItem(BACKUP_KEY);
      return;
    }

    // 복원 진행 중 — 로딩 오버레이로 미완성 장바구니를 가림
    this.#showRestoreOverlay();

    try {
      if (hasFullOrder) {
        const clearRes = await fetch(`${Theme.routes.cart_url}/clear.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({}),
        });
        if (!clearRes.ok) throw new Error('Cart clear failed');

        const addOrder = [...backup.fullLineOrder].reverse();
        for (const line of addOrder) {
          const payloadItem = this.#normalizeBackupLine(line);
          const addRes = await fetch(Theme.routes.cart_add_url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [payloadItem] }),
          });
          if (!addRes.ok) throw new Error('Cart add failed');
        }
      } else {
        const response = await fetch(Theme.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: backup.items }),
        });
        if (!response.ok) throw new Error('Cart add failed');
      }

      localStorage.removeItem(BACKUP_KEY);
      window.location.reload();
    } catch {
      localStorage.removeItem(BACKUP_KEY);
      this.#removeRestoreOverlay();
    }
  }

  /**
   * 체크박스 data-*에서 Cart add.js용 라인 객체 생성 (화면 순서 = DOM 순서)
   * @param {HTMLInputElement} cb
   */
  #lineItemFromCheckbox(cb) {
    const id = parseInt(cb.dataset.variantId ?? '0', 10);
    const quantity = parseInt(cb.dataset.quantity ?? '0', 10);
    /** @type {Record<string, unknown>} */
    let properties = {};
    const rawProps = cb.dataset.lineProperties;
    if (rawProps) {
      try {
        properties = /** @type {Record<string, unknown>} */ (JSON.parse(rawProps));
      } catch {
        properties = {};
      }
    }
    /** @type {{ id: number, quantity: number, properties: Record<string, unknown>, selling_plan?: number }} */
    const out = { id, quantity, properties };
    const sp = cb.dataset.sellingPlan;
    if (sp) {
      const n = parseInt(sp, 10);
      if (!Number.isNaN(n) && n > 0) {
        out.selling_plan = n;
      }
    }
    return out;
  }

  /**
   * localStorage 백업 한 줄을 cart/add.js 페이로드 형태로 정규화
   * @param {Record<string, unknown>} line
   */
  #normalizeBackupLine(line) {
    const id = typeof line.id === 'number' ? line.id : parseInt(String(line.id ?? '0'), 10);
    const quantity =
      typeof line.quantity === 'number' ? line.quantity : parseInt(String(line.quantity ?? '0'), 10);
    /** @type {Record<string, unknown>} */
    const properties =
      line.properties && typeof line.properties === 'object' && !Array.isArray(line.properties)
        ? /** @type {Record<string, unknown>} */ (line.properties)
        : {};
    /** @type {{ id: number, quantity: number, properties: Record<string, unknown>, selling_plan?: number }} */
    const out = { id, quantity, properties };
    const sp = line.selling_plan;
    if (sp != null && sp !== '') {
      const n = typeof sp === 'number' ? sp : parseInt(String(sp), 10);
      if (!Number.isNaN(n) && n > 0) {
        out.selling_plan = n;
      }
    }
    return out;
  }

  #showRestoreOverlay() {
    const overlay = document.createElement('div');
    overlay.id = 'cart-restore-overlay';
    overlay.innerHTML = `
      <div class="cart-restore-overlay__content">
        <div class="cart-restore-overlay__spinner"></div>
        <p class="cart-restore-overlay__text">장바구니를 복원하고 있습니다...</p>
      </div>
    `;
    document.body.appendChild(overlay);
  }

  #removeRestoreOverlay() {
    document.getElementById('cart-restore-overlay')?.remove();
  }

  // ---------------------------------------------------------------------------
  // Checkbox initialisation
  // ---------------------------------------------------------------------------

  #initCheckboxes() {
    // 항상 HTML 기본값(checked) 사용 - sessionStorage 복원하지 않음
    this.#syncSelectedKeysFromDOM();
    this.#syncSelectAllCheckbox();
  }

  /** Read checked state from DOM into #selectedKeys */
  #syncSelectedKeysFromDOM() {
    this.#selectedKeys.clear();
    this.#getItemCheckboxes().forEach((cb) => {
      if (cb.checked && !cb.disabled) {
        this.#selectedKeys.add(cb.dataset.itemKey ?? '');
      }
    });
    this.#saveSelectedState();
  }

  // ---------------------------------------------------------------------------
  // Event binding
  // ---------------------------------------------------------------------------

  #bindEvents() {
    document.addEventListener('change', this.#handleChange);
    document.addEventListener('click', this.#handleClick);
    document.addEventListener('cart:morph-complete', this.#handleMorphComplete);
  }

  /** @param {Event} e */
  #handleChange = (e) => {
    const target = /** @type {HTMLInputElement} */ (e.target);

    if (target.classList.contains('cart-item-checkbox')) {
      this.#onItemToggle(target);
    } else if (target.classList.contains('cart-item-checkbox-all')) {
      this.#onToggleAll(target.checked);
    }
  };

  /** @param {Event} e */
  #handleClick = (e) => {
    const btn = /** @type {HTMLElement} */ (e.target).closest('#selective-checkout-btn');
    if (btn) {
      e.preventDefault();
      this.#handleCheckout();
    }
  };

  #handleMorphComplete = () => {
    this.#restoreCheckboxStates();
    this.#updateSummary();
  };

  // ---------------------------------------------------------------------------
  // Checkbox logic
  // ---------------------------------------------------------------------------

  /** @param {HTMLInputElement} checkbox */
  #onItemToggle(checkbox) {
    const key = checkbox.dataset.itemKey ?? '';
    if (checkbox.checked) {
      this.#selectedKeys.add(key);
    } else {
      this.#selectedKeys.delete(key);
    }
    this.#syncSelectAllCheckbox();
    this.#saveSelectedState();
    this.#updateSummary();
  }

  /** @param {boolean} checked */
  #onToggleAll(checked) {
    this.#getItemCheckboxes().forEach((cb) => {
      const key = cb.dataset.itemKey ?? '';

      if (cb.disabled) {
        cb.checked = false;
        if (key) {
          this.#selectedKeys.delete(key);
        }
        return;
      }

      cb.checked = checked;
      if (checked) {
        this.#selectedKeys.add(key);
      } else {
        this.#selectedKeys.delete(key);
      }
    });
    this.#syncSelectAllCheckbox();
    this.#saveSelectedState();
    this.#updateSummary();
  }

  #syncSelectAllCheckbox() {
    const selectAll = /** @type {HTMLInputElement|null} */ (
      document.querySelector('.cart-item-checkbox-all')
    );
    if (!selectAll) return;

    const checkboxes = this.#getItemCheckboxes().filter((cb) => !cb.disabled);
    if (checkboxes.length === 0) {
      selectAll.checked = false;
      return;
    }
    selectAll.checked = checkboxes.every((cb) => cb.checked);
  }

  /** Restore checkbox states after morphSection replaces DOM */
  #restoreCheckboxStates() {
    const checkboxes = this.#getItemCheckboxes();
    const currentKeys = new Set(checkboxes.map((cb) => cb.dataset.itemKey));

    // Remove keys that no longer exist in DOM (item was deleted)
    for (const key of this.#selectedKeys) {
      if (!currentKeys.has(key)) {
        this.#selectedKeys.delete(key);
      }
    }

    checkboxes.forEach((cb) => {
      const key = cb.dataset.itemKey ?? '';

      if (cb.disabled) {
        cb.checked = false;
        this.#selectedKeys.delete(key);
        return;
      }

      cb.checked = this.#selectedKeys.has(key);
    });

    this.#syncSelectAllCheckbox();
    this.#saveSelectedState();
  }

  // ---------------------------------------------------------------------------
  // Summary UI update
  // ---------------------------------------------------------------------------

  #updateSummary() {
    const checkboxes = this.#getItemCheckboxes().filter((cb) => !cb.disabled);
    let selectedSubtotal = 0;
    let selectedTotal = 0;
    let selectedCountForSummary = 0;
    let selectedCountForDelete = 0;

    checkboxes.forEach((cb) => {
      if (!cb.checked) return;
      const isSoldOut = cb.dataset.soldOut === 'true';
      if (!isSoldOut) {
        selectedSubtotal += parseInt(cb.dataset.originalPrice || '0', 10);
        selectedTotal += parseInt(cb.dataset.itemPrice || '0', 10);
        selectedCountForSummary++;
      }
      selectedCountForDelete++;
    });

    const selectedDiscount = selectedSubtotal - selectedTotal;

    const totalCount = checkboxes.length;
    this.#updatePriceElement('[data-selected-subtotal]', selectedSubtotal);
    this.#updateDiscountRow(selectedDiscount);
    this.#updatePriceElement('[data-selected-total]', selectedTotal);
    this.#updateSelectedCount(selectedCountForSummary);
    this.#updateSelectCount(selectedCountForSummary, totalCount);
    this.#updateCheckoutButton(selectedCountForSummary);
    this.#updateDeleteSelectedButton(selectedCountForDelete);
  }

  /**
   * @param {string} selector
   * @param {number} amountInCents
   */
  #updatePriceElement(selector, amountInCents) {
    const el = document.querySelector(selector);
    if (!el) return;

    const formatted = this.#formatMoney(amountInCents);
    el.textContent = formatted;

    if (el.hasAttribute('value')) {
      el.setAttribute('value', formatted);
    }
  }

  /** @param {number} discount */
  #updateDiscountRow(discount) {
    const row = /** @type {HTMLElement|null} */ (document.querySelector('[data-discount-row]'));
    const valueEl = document.querySelector('[data-selected-discount]');

    if (!row || !valueEl) return;

    if (discount > 0) {
      row.style.display = '';
      valueEl.textContent = `- ${this.#formatMoney(discount)}`;
    } else {
      row.style.display = 'none';
    }
  }

  /** @param {number} count */
  #updateSelectedCount(count) {
    const el = document.querySelector('[data-selected-count]');
    if (!el) return;
    el.textContent = count > 0 ? `${count}개 상품 선택` : '';
  }

  /**
   * @param {number} selected
   * @param {number} total
   */
  #updateSelectCount(selected, total) {
    const el = document.querySelector('[data-select-count]');
    if (!el) return;
    el.textContent = `(${selected}/${total})`;
  }

  /** @param {number} count */
  #updateCheckoutButton(count) {
    const btn = /** @type {HTMLButtonElement|null} */ (
      document.querySelector('#selective-checkout-btn')
    );
    if (!btn) return;
    btn.disabled = count === 0;
  }

  /** @param {number} count */
  #updateDeleteSelectedButton(count) {
    const btn = /** @type {HTMLButtonElement|null} */ (
      document.querySelector('.cart-items__delete-all-button')
    );
    if (!btn) return;
    btn.disabled = count === 0;
  }

  // ---------------------------------------------------------------------------
  // Checkout process
  // ---------------------------------------------------------------------------

  async #handleCheckout() {
    if (this.#selectedKeys.size === 0) {
      alert('결제할 상품을 선택해주세요.');
      return;
    }

    const allCheckboxes = this.#getItemCheckboxes();
    const excludedFromCheckout = allCheckboxes.filter(
      (cb) => cb.dataset.checkoutBlocked === 'true' || (!cb.checked && !cb.disabled)
    );

    if (excludedFromCheckout.length === 0) {
      // All items selected — proceed with normal checkout
      window.location.href = '/checkout';
      return;
    }

    // Backup: 결제 제외 + 결제 직전 전체 라인 순서(위→아래, 복원 시 역순 추가로 순서 유지)
    const fullLineOrder = allCheckboxes.map((cb) => this.#lineItemFromCheckbox(cb));
    const backupItems = excludedFromCheckout.map((cb) => this.#lineItemFromCheckbox(cb));

    localStorage.setItem(
      BACKUP_KEY,
      JSON.stringify({ timestamp: Date.now(), items: backupItems, fullLineOrder })
    );

    // Build updates: set checkout-excluded items' quantities to 0
    /** @type {Record<string, number>} */
    const updates = {};
    excludedFromCheckout.forEach((cb) => {
      const key = cb.dataset.itemKey;
      if (key) updates[key] = 0;
    });

    try {
      const btn = document.querySelector('#selective-checkout-btn');
      if (btn) {
        btn.setAttribute('disabled', '');
        btn.textContent = '처리 중...';
      }

      const response = await fetch(Theme.routes.cart_update_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      });

      if (!response.ok) throw new Error('Cart update failed');

      // Clear selected state for next visit
      sessionStorage.removeItem(SELECTED_STATE_KEY);

      window.location.href = '/checkout';
    } catch (err) {
      console.error('Selective checkout failed:', err);
      localStorage.removeItem(BACKUP_KEY);
      alert('결제 처리 중 오류가 발생했습니다. 다시 시도해주세요.');

      const btn = document.querySelector('#selective-checkout-btn');
      if (btn) {
        btn.removeAttribute('disabled');
        btn.textContent = '결제하기';
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  /** @returns {HTMLInputElement[]} */
  #getItemCheckboxes() {
    return /** @type {HTMLInputElement[]} */ ([
      ...document.querySelectorAll('.cart-item-checkbox'),
    ]);
  }

  /** @param {number} cents */
  #formatMoney(cents) {
    const tpl = /** @type {HTMLTemplateElement|null} */ (document.querySelector('[data-money-format]'));
    const format = tpl?.content?.textContent?.trim() || '₩{{amount_no_decimals}}';

    const amountNoDecimals = Math.round(cents / 100).toLocaleString('ko-KR');
    const amount = (cents / 100).toLocaleString('ko-KR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return format
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimals.replace('.', ','))
      .replace('{{amount_no_decimals}}', amountNoDecimals)
      .replace('{{amount}}', amount);
  }

  #saveSelectedState() {
    sessionStorage.setItem(
      SELECTED_STATE_KEY,
      JSON.stringify([...this.#selectedKeys])
    );
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new CartSelectiveCheckout());
} else {
  new CartSelectiveCheckout();
}
